/**
 * Packages & Services builder — data-loading hook. Extracted verbatim
 * from the former AgencyServicesPage monolith (Task 150-A): same
 * `Promise.all` load, same `AbortController` guard, same `reloadKey`
 * re-fetch, same `LoadState` union. No behavior change.
 */

import { useEffect, useState } from 'react';
import { listAgencyPackages, listAgencyServices } from '../../api/endpoints';
import type { AgencyPackageRow, AgencyServiceRow } from '../../api/types';

export type LoadState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly services: ReadonlyArray<AgencyServiceRow>;
      readonly packages: ReadonlyArray<AgencyPackageRow>;
    }
  | { readonly status: 'error'; readonly error: unknown };

export function useServicesPackages(): {
  readonly state: LoadState;
  readonly refresh: () => void;
} {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    setState({ status: 'loading' });
    Promise.all([
      listAgencyServices({ signal: ctrl.signal }),
      listAgencyPackages({ signal: ctrl.signal }),
    ])
      .then(([s, p]) => {
        if (ctrl.signal.aborted) return;
        setState({ status: 'ready', services: s.services, packages: p.packages });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setState({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [reloadKey]);

  const refresh = () => setReloadKey((n) => n + 1);
  return { state, refresh };
}
